import {
  Box,
  Text,
  Image,
  Flex,
  Heading,
  useColorModeValue,
  HStack,
  Circle,
  Link,
} from '@chakra-ui/react';
import { LOGO_BASE64, QR_BASE64 } from '../../../utils/base64';
import footerLight from '../../../assets/vectors/footer-light.svg';
import footerDark from '../../../assets/vectors/footer-dark.svg';
import { downloadPageTranslations } from '../../../utils/downloadPageTranslations';
import { CustomHelpIcon } from '../../../theme/icons/CustomHelpIcon';

const PreviewWebsitePage = ({ listItem, previewData, editingLang }) => {
  const headerBg = useColorModeValue('gray.300', 'gray.700');
  const t =
    downloadPageTranslations[editingLang] || downloadPageTranslations.en;
  return (
    <Flex direction="column" w="100%" align="center">
      {!listItem && (
        <Text
          textStyle="bodyBold"
          fontSize="16px"
          color="secondaryDark"
          textAlign={{ base: 'center', md: 'left' }}
          mt={4}
        >
          Website page
        </Text>
      )}
      <Flex
        mt={listItem ? '0px' : '15px'}
        w="100%"
        maxW={{ base: '320px', sm: '500px', md: listItem ? '500px' : '600px' }}
        align="center"
        justify="space-between"
        p={2.5}
        bg={headerBg}
        borderRadius="10px 10px 0px 0px"
      >
        <HStack pr={2} spacing={2} display="flex">
          <Circle size="12px" bg="#ff5f56" />
          <Circle size="12px" bg="#ffbd2e" />
          <Circle size="12px" bg="#27c93f" />
        </HStack>
      </Flex>
      <Box
        data-testid="preview-website-page"
        w="100%"
        maxW={{ base: '320px', sm: '500px', md: listItem ? '500px' : '600px' }}
        boxShadow="10px 15px 15px rgb(0 0 0 / 15%)"
        borderRadius="0px 0px 10px 10px"
        bg={previewData.backgroundColor}
        minH={listItem ? '400px' : '550px'}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        p={listItem ? { base: 8, md: 12 } : { base: 4, md: 8 }}
        position="relative"
      >
        {/* Header text */}
        {previewData.headerText && (
          <Text
            fontFamily="avenir"
            data-testid="header-text"
            textStyle="headingFamilyMedium"
            fontSize={
              listItem
                ? { base: '14px', sm: '18px' }
                : { base: '18px', sm: '22px' }
            }
            fontWeight="600"
            color={previewData.headerColor}
            textAlign="center"
            mb={{ base: 4, md: 6 }}
          >
            {editingLang
              ? previewData.translations[editingLang].headerText
              : previewData.headerText}
          </Text>
        )}
        <Flex
          data-testid="download-info-card"
          direction="column"
          justifyContent="center"
          alignItems="center"
          bg="white"
          borderRadius="15px"
          width="100%"
          maxW={
            listItem
              ? { base: '200px', sm: '250px' }
              : { base: '200px', sm: '300px' }
          }
          mx="auto"
          py={
            listItem
              ? { base: '10px', sm: '15px' }
              : { base: '20px', sm: '25px' }
          }
          px={
            listItem
              ? { base: '15px', sm: '20px' }
              : { base: '15px', sm: '30px' }
          }
          mb={listItem ? '20px' : '0px'}
        >
          <Image
            data-testid="logo-image"
            width={listItem ? '130px' : '180px'}
            src={previewData.logoImage || LOGO_BASE64}
            alt="pass-download-web-page"
          />
          {/* Pass Download Text */}
          {previewData.passDownloadText && (
            <Text
              fontFamily="avenir"
              textAlign="center"
              mt={listItem ? 2 : 4}
              data-testid="body-text"
              textStyle="bodyRegular"
              fontSize={
                listItem
                  ? { base: '10px', sm: '12px' }
                  : { base: '10px', sm: '14px' }
              }
              lineHeight="120%"
              color={
                listItem
                  ? previewData.passDownloadTextColor
                  : previewData.passDownloadTextColor
              }
            >
              {editingLang
                ? previewData.translations[editingLang].passDownloadText
                : previewData.passDownloadText}
            </Text>
          )}
          {/* QR Code */}
          <Image
            mt={3}
            data-testid="qr-code-image"
            boxSize={
              listItem
                ? { base: '50px', sm: '65px' }
                : { base: '50px', sm: '80px' }
            }
            src={QR_BASE64}
            alt="pass-download-web-page"
          />

          <Heading
            fontFamily="avenir"
            size="lg"
            fontWeight="black"
            fontSize={
              listItem
                ? { base: '12px', sm: '14px' }
                : { base: '12px', sm: '16px' }
            }
            color={previewData.passDownloadTextColor || '#3C4852'}
            mt={2}
            textAlign="center"
          >
            {t.scanFirstLine}
          </Heading>
          <Text
            fontFamily="avenir"
            fontSize={
              listItem
                ? { base: '8px', sm: '10px' }
                : { base: '10px', sm: '12px' }
            }
            color={previewData.passDownloadTextColor || '#3C4852'}
          >
            {t.scanSecondLine}
          </Text>
          {/* Help and FAQs link */}
          {previewData.clientHelpLink?.url &&
            previewData.clientHelpLink?.display_text !== '' && (
              <Flex
                direction="row"
                align="center"
                gap={1}
                mt={2}
                _hover={{
                  textDecoration: 'underline',
                  textDecorationColor:
                    previewData.passDownloadTextColor || '#45525B',
                }}
              >
                <CustomHelpIcon
                  boxSize={'10px'}
                  color={previewData.componentFillColor}
                />
                <Link
                  color={previewData.passDownloadTextColor || '#45525B'}
                  fontSize={listItem ? '9px' : '10px'}
                  href="#"
                  position="relative"
                >
                  <Text fontFamily="avenir" fontWeight="bold">
                    {editingLang
                      ? previewData.translations[editingLang].clientHelpLink
                      : previewData.clientHelpLink?.display_text}
                  </Text>
                </Link>
              </Flex>
            )}
        </Flex>
        {/* Footer */}
        {!previewData.skipFooter && (
          <Box
            data-testid="passentry-footer"
            position="absolute"
            bottom={listItem ? '18px' : '20px'}
            left="50%"
            transform="translateX(-50%)"
          >
            <Image
              width={listItem ? '120px' : '150px'}
              src={
                previewData.footerColor === 'dark' ? footerDark : footerLight
              }
              alt="pass-download-web-page"
            />
          </Box>
        )}
      </Box>
    </Flex>
  );
};

export default PreviewWebsitePage;
